// EXPORTS: ITodayDiet, useTodayDiet, IUserProfile, useUserProfile, IAppState, useAppState, useTodayWorkout, ITodayWorkoutInfo, IDietPlan, useDietPlans, IDailyHealthRecord, useMemberTrainingPlan

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import userStorage, { getCurrentUserId } from '@/lib/user-storage';
import type { CourseSystem, ICourse, ICourseDay } from '@/data/courses';
import { MOCK_COURSES } from '@/data/courses';
import { generateTrainingPlan, type ITrainingPlan, type ITrainingPlanDay, type TrainingDayType, getCarbDayFromTrainingDay } from '@/data/training-plan-generator';
import { getWeightSuggestion, detectLayoff, getConsecutiveUnderperformCount, type WeightSuggestion } from '@/lib/progressive-overload';
import { generateInjuryRecoveryPlan, getTodayRecoveryPlan, INJURY_ACTION_POOL, INJURY_PART_MAP, INJURY_AVOID_MAP, INJURY_PART_LABEL_REVERSE, DAILY_RELAX_ACTIONS, type IRecoveryPlan, type InjuryPart } from '@/data/injury-recovery-plan';
import { getExerciseById } from '@/data/exercises';
import { matchesLibraryHistoryExercise, findCompatibleLibraryPR, recordsForLibraryHistoryQuery } from '@/lib/libraryHistoryReferences';
import type { IMealFood, MealType } from '@/data/foods';
import type { DietPlanTemplate } from '@/data/diet-plan-templates';
import { calcPlanDailyTargets, generatePlanMealFoods, DIET_PLAN_TEMPLATES } from '@/data/diet-plan-templates';
import { useActiveWorkout } from './useActiveWorkout';
import { useDailyHealth } from './useDailyHealth';
import { useMemberStatus } from './useMemberStatus';
import { useMemberTrainingPlan } from './useMemberTrainingPlan';
import { localTrainingDate } from '@/lib/memberTrainingDay';
import { planStartDateTimestamp } from '@/lib/planStartDate';
import { effectiveDietPlan, normalizeDietPlans, decodeDietPlanId, isRetiredFatLoss, type DietDaySnapshot } from '@/lib/dietService';
import { attachedPlanIssue, isAttachedFatLoss } from '@/data/attached-fat-loss';
import { useDietServiceDay } from './useDietServiceDay';
import { resolveDietDate } from '@/lib/dietDate';
import { scheduleFields, type PersonalSchedule } from '@/lib/personalSchedule';
import { legacyDietDay } from '@/lib/dietLegacyDay';
import { assessmentWeeklyHours } from '@/lib/assessmentWeeklyHours';
import { trainingNutritionHours, futureNutritionHours } from '@/lib/trainingNutritionHours';

// ==================== 跨实例状态同步（EventBus） ====================
// 解决同一 hook 多实例调用时各实例 state 独立、数据不同步的问题
// 用法：写操作时 emit 事件，其他实例监听后从 storage 重新读取

interface SyncEventMap {
  [key: string]: unknown;
}

const syncListeners = new Map<string, Set<() => void>>();

function emitSync(key: string) {
  const set = syncListeners.get(key);
  if (set) set.forEach(fn => fn());
}

function onSync(key: string, fn: () => void) {
  if (!syncListeners.has(key)) syncListeners.set(key, new Set());
  syncListeners.get(key)!.add(fn);
  return () => {
    syncListeners.get(key)?.delete(fn);
  };
}

/**
 * 让 useState 支持跨组件同步：写入时 emit 事件，其他实例监听后重读 storage
 * @param key storage key
 * @param defaultValue 初始值或初始值工厂函数
 */
export function useSyncedState<T>(
  key: string,
  defaultValue: T | (() => T),
): [T, (updater: T | ((prev: T) => T), options?: { silent?: boolean }) => void] {
  const owner = getCurrentUserId();
  const [state, setState] = useState<T>(() => {
    try {
      const raw = userStorage.getItem(key);
      if (raw != null) return JSON.parse(raw) as T;
    } catch { /* fallthrough */ }
    return typeof defaultValue === 'function'
      ? (defaultValue as () => T)()
      : defaultValue;
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // 监听从其他实例发来的同步事件：从 storage 重新读取
  useEffect(() => {
    const off = onSync(key, () => {
      try {
        const raw = userStorage.getItem(key);
        if (raw != null) {
          const parsed = JSON.parse(raw) as T;
          setState(parsed);
        }
      } catch { /* ignore */ }
    });
    return off;
  }, [key]);

  const updateState = useCallback(
    (updater: T | ((prev: T) => T), options?: { silent?: boolean }) => {
      if (getCurrentUserId() !== owner) return;
      setState(prev => {
        if (getCurrentUserId() !== owner) return prev;
        const next = typeof updater === 'function'
          ? (updater as (p: T) => T)(prev)
          : updater;
        // 写 storage
        try {
          userStorage.setItem(key, JSON.stringify(next));
        } catch { /* ignore */ }
        // 通知其他实例同步（默认广播，silent 时不广播）
        if (!options?.silent) {
          // 微任务里广播，避免同一次 render 里重复触发
          queueMicrotask(() => emitSync(key));
        }
        return next;
      });
    },
    [key, owner],
  );

  return [state, updateState];
}

// ==================== 用户资料 & 身体数据 ====================

export interface IUserProfile extends PersonalSchedule {
  name: string;
  age?: number;
  sessionMinutes?: number;
  avatar?: string;
  gender: 'male' | 'female';
  height: number;      // cm
  weight: number;      // kg
  goalWeight?: number; // 目标体重 kg，可选
  trainingMonths: number; // 训练经验(月)
  bodyType: 'slim' | 'normal' | 'overweight';
  goal: 'fat-loss' | 'muscle-gain' | 'shape';
  trainingFrequency: '1-2' | '3-4' | '5-6';
  weeklyTrainingHours: number; // 每周训练总时长(小时)
  trainingPlace: 'gym' | 'home';
  trainingTimeSlot: 'morning-early' | 'morning' | 'noon' | 'afternoon' | 'evening' | 'night';
  hasInjury: boolean;
  injuryDetails: string[];
  injuryParts: string[];   // 伤病部位：肩/膝/腰/腕/踝/颈/肘/髋
  allergens: string[];     // 过敏原（兼容历史字段，不再采集）
  avoidFoods: string[];    // 不吃的食物（含过敏/忌口/偏好），驱动餐单替换
  sleepQuality: 'good' | 'normal' | 'poor';
  sleepBedtime: 'early' | 'normal' | 'late' | 'very-late'; // 入睡时间
  dietPreference: string[];
  tastePreferenceList: string[]; // 口味偏好（多选，私人订制问卷采集）
  mealsPerDay: 3 | 4 | 5;  // 每日餐次（5 餐兼容历史数据，展示与生成按 4 餐归一化）
  supplements: string[];   // 使用中的补剂
  strengthDays: number[];  // 用户勾选的每周力量训练日，1=周一 ... 7=周日
  planStartDate: string;   // 计划开始日期（YYYY-MM-DD），空字符串表示今天
  restingHeartRate: number; // 晨脉 bpm
  sleepHours: number;      // 睡眠时长 h
}

const DEFAULT_PROFILE: IUserProfile = {
  name: '',
  gender: 'male',
  height: 170,
  weight: 65,
  goalWeight: undefined,
  trainingMonths: 0,
  bodyType: 'normal',
  goal: 'fat-loss',
  trainingFrequency: '3-4',
  weeklyTrainingHours: 3,
  trainingPlace: 'gym',
  trainingTimeSlot: 'evening',
  hasInjury: false,
  injuryDetails: [],
  injuryParts: [],
  allergens: [],
  avoidFoods: [],
  sleepQuality: 'normal',
  sleepBedtime: 'normal',
  dietPreference: [],
  tastePreferenceList: [],
  mealsPerDay: 4,
  supplements: [],
  strengthDays: [1, 3, 5],
  planStartDate: '',
  restingHeartRate: 0,
  sleepHours: 0,
};

const PROFILE_KEY = 'user_profile';

// ==================== 饮食方案（单方案） ====================

export interface IDietPlan extends PersonalSchedule {
  weeklyHoursHistory?: import("@/lib/trainingScheduleHistory").WeeklyHoursVersion[];
  age?: number;
  activityFactor?: number;
  memberDietSuspended?: boolean;
  memberAccessUntil?: string;
  serviceNotice?: 'expired' | 'retired' | 'completed' | 'reassessment';
  dailySnapshots?: Record<string, DietDaySnapshot>;
  id: string;
  name: string;
  goal: 'fat-loss' | 'muscle-gain' | 'shape';
  /** 方案模板：generic 通用公式 / three-month 三个月 / new-year-40 过年40天 / triple-carb 三倍碳水 */
  planTemplate: DietPlanTemplate;
  /** 方案开始日期（时间戳 ms），用于计算第几天 */
  startDate: number;
  tastePreference: string;
  avoidFoods: string[];
  allergens: string[];      // 过敏原（私人订制问卷采集，忌口替换扩展参考）
  mealsPerDay: 3 | 4 | 5;   // 每日餐次
  supplements: string[];    // 使用中的补剂（方案调配参考）
  gender: 'male' | 'female';
  height: number;
  weight: number;
  weeklyHours: number;
  bodyType: 'slim' | 'normal' | 'overweight';
  carbDay: 'high' | 'medium' | 'low';
  trainingTimeSlot: 'morning-early' | 'morning' | 'noon' | 'afternoon' | 'evening' | 'night';
  trainingVenue?: 'gym' | 'home';
  mealFoods: {
    breakfast: IMealFood[];
    lunch: IMealFood[];
    dinner: IMealFood[];
    snack: IMealFood[];
    'pre-workout'?: IMealFood[];
    'post-workout'?: IMealFood[];
  };
  createdAt: number;
  // —— 以下字段为历史数据兼容，新流程不再生成 ——
  // 旧 AI 周计划相关字段（保留类型兼容旧数据，UI 不再展示）
  aiWeekPlanGenerated?: boolean;
  // 旧 AI 生成的每周饮食计划 Markdown（保留兼容）
  aiWeeklyPlanMarkdown?: string;
  // 会员专属：是否已通过详细问卷生成
  fromDetailedAssessment?: boolean;
  // 旧 AI 生成的结构化 7 天周餐单（保留兼容）
  // 生成当天为 day1，依次顺延 7 天；饮食页当日餐单直接从这里读取
  aiWeeklyMeals?: Record<string, Record<MealType, IMealFood[]>>;
  dailyMealFoods?: Record<string, Partial<Record<MealType, IMealFood[]>>>;
}

const DIET_PLANS_KEY = 'diet_plans';
const CURRENT_PLAN_KEY = 'current_diet_plan_id';
const DEFAULT_PLAN_ID = 'default';
const PLAN_ID = DEFAULT_PLAN_ID;

function generatePlanId() {
  return 'plan_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// 构建默认方案
function buildDefaultPlan(profile: IUserProfile): IDietPlan {
  const planTemplate: DietPlanTemplate = 'generic';
  const targets = calcPlanDailyTargets({
    template: planTemplate,
    weight: profile.weight,
    gender: profile.gender,
    height: profile.height,
    bodyType: profile.bodyType,
    weeklyHours: profile.weeklyTrainingHours || 4,
    goal: profile.goal,
    carbDay: 'medium',
  });
  const mealPlan = generatePlanMealFoods(planTemplate, targets, profile.gender);
  const mealFoods = {
    breakfast: mealPlan.breakfast.map(m => ({ foodId: m.foodId, grams: m.grams })),
    lunch: mealPlan.lunch.map(m => ({ foodId: m.foodId, grams: m.grams })),
    dinner: mealPlan.dinner.map(m => ({ foodId: m.foodId, grams: m.grams })),
    snack: mealPlan.snack.map(m => ({ foodId: m.foodId, grams: m.grams })),
  };
  return {
    id: PLAN_ID,
    name: profile.goal === 'muscle-gain' ? '通用增肌方案' :
          profile.goal === 'shape' ? '通用塑形方案' :
          '通用减脂方案',
    goal: profile.goal,
    planTemplate,
    startDate: Date.now(),
  tastePreference: '正常',
  avoidFoods: [],
  allergens: [],
  mealsPerDay: 4 as const,
  supplements: [],
  gender: profile.gender,
    height: profile.height,
    weight: profile.weight,
    weeklyHours: profile.weeklyTrainingHours || 4,
    bodyType: profile.bodyType,
    carbDay: 'medium',
    trainingTimeSlot: profile.trainingTimeSlot,
    mealFoods,
    createdAt: Date.now(),
    aiWeekPlanGenerated: false,
  };
}

export function useDietPlans() {
  const dietOwner = getCurrentUserId();
  const serviceDay = useDietServiceDay();
  const { profile, setProfile } = useUserProfile();
  const { member } = useMemberStatus();

  const [storedPlans, setPlans] = useSyncedState<IDietPlan[]>(DIET_PLANS_KEY, () => {
    try {
      // 优先读多方案数组格式
      const raw = userStorage.getItem(DIET_PLANS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const normalized = parsed.map(p => ({
            ...p,
            planTemplate: (p.planTemplate || 'generic') as DietPlanTemplate,
            startDate: p.startDate || Date.now(),
            aiWeekPlanGenerated: p.aiWeekPlanGenerated || false,
            goal: (p.goal === 'maintain' ? 'shape' : p.goal) as IDietPlan['goal'],
          }));
          return normalized;
        }
      }
       // 兼容：旧单方案格式
       const oldRaw = userStorage.getItem('diet_plan') || userStorage.getItem('diet_plan_storage');
       if (oldRaw) {
         try {
           const p = JSON.parse(oldRaw);
           if (p && p.id) {
             return [{
               ...p,
               planTemplate: (p.planTemplate || 'generic') as DietPlanTemplate,
               startDate: p.startDate || Date.now(),
               aiWeekPlanGenerated: p.aiWeekPlanGenerated || false,
               goal: (p.goal === 'maintain' ? 'shape' : p.goal) as IDietPlan['goal'],
             }];
           }
         } catch { /* ignore */ }
       }
       return [];
    } catch {
      return [];
    }
  });

  const plans = useMemo(() => normalizeDietPlans(storedPlans), [storedPlans]);
  const [currentPlanId, setCurrentPlanIdState] = useState<string>(() => {
    try {
      const saved = userStorage.getItem(CURRENT_PLAN_KEY);
      if (saved) return decodeDietPlanId(saved);
      const raw = userStorage.getItem(DIET_PLANS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) return arr[0].id;
      }
      return DEFAULT_PLAN_ID;
    } catch {
      return DEFAULT_PLAN_ID;
    }
  });

  const setCurrentPlanId = useCallback((id: string) => {
    if (getCurrentUserId() !== dietOwner) return;
    userStorage.setItem(CURRENT_PLAN_KEY, id);
    setCurrentPlanIdState(id);
    queueMicrotask(() => emitSync(CURRENT_PLAN_KEY));
  }, [dietOwner]);
  useEffect(() => onSync(CURRENT_PLAN_KEY, () => {
    const id = userStorage.getItem(CURRENT_PLAN_KEY);
    if (id) setCurrentPlanIdState(decodeDietPlanId(id));
  }), []);
  const savedCurrentPlan = plans.find(p => p.id === currentPlanId) || plans[0] || null;
  const currentPlan = useMemo(() => effectiveDietPlan(savedCurrentPlan, profile, member.isMember), [savedCurrentPlan, profile, member.isMember, serviceDay]);
  useEffect(() => {
    if (!savedCurrentPlan || !currentPlan?.serviceNotice) return;
    const snapshot = legacyDietDay(savedCurrentPlan, profile);
    if (!snapshot) return;
    setPlans(previous => {
      if (previous.some(p => p.dailySnapshots?.[serviceDay])) return previous;
      return previous.map(p => p.id === savedCurrentPlan.id ? { ...p, dailySnapshots: { ...p.dailySnapshots, [serviceDay]: snapshot } } : p);
    });
  }, [savedCurrentPlan, currentPlan?.serviceNotice, profile, serviceDay, setPlans]);
  useEffect(() => {
    const today = localTrainingDate();
    const expired = (p: IDietPlan) => p.planTemplate !== 'generic' && !p.memberDietSuspended
      && (!member.isMember || (!!p.memberAccessUntil && p.memberAccessUntil < today));
    const extended = (p: IDietPlan) => !expired(p) && member.isMember && !p.memberDietSuspended
      && p.planTemplate !== 'generic' && !!member.expireDate && p.memberAccessUntil !== member.expireDate;
    if (plans.some(p => expired(p) || extended(p))) {
      setPlans(previous => previous.map(p => expired(p) ? { ...p, memberDietSuspended: true }
        : extended(p) ? { ...p, memberAccessUntil: member.expireDate } : p));
    }
  }, [member.isMember, member.expireDate, plans, setPlans, serviceDay]);
  // 向后兼容：plan 是 currentPlan 的别名
  const plan = currentPlan;
  const hasPlan = plans.length > 0;
  const hasPlans = plans.length > 0;

  // 更新当前方案（向后兼容：单参数形式直接作用于当前方案）
  // 支持两种调用：updatePlan(updates) 或 updatePlan(planId, updates)
  const updatePlan = useCallback((arg1: string | Partial<IDietPlan>, arg2?: Partial<IDietPlan>) => {
    const planId = typeof arg1 === 'string' ? arg1 : (currentPlan?.id || plans[0]?.id);
    const updates = typeof arg1 === 'string' ? (arg2 || {}) : arg1;
    if (!planId) return;
    setPlans(prev => prev.map(p => p.id === planId ? { ...p, ...updates } : p));
  }, [currentPlan, plans]);

  const captureDietDay = useCallback((dateKey: string, snapshot: DietDaySnapshot) => {
    if (dateKey !== new Date().toDateString()) return;
    setPlans(previous => {
      if (previous.some(p => p.dailySnapshots?.[dateKey])) return previous;
      return previous.map(p => p.id === savedCurrentPlan?.id ? { ...p, dailySnapshots: { ...p.dailySnapshots, [dateKey]: snapshot } } : p);
    });
  }, [savedCurrentPlan?.id, setPlans]);

  // 新建当前方案；旧方案保留用于历史记录，普通用户仍只有通用方案权益。
  const addPlan = useCallback((planData: Partial<IDietPlan> & { name: string }): IDietPlan | null => {
    const goalLabel = { 'fat-loss': '减脂', 'muscle-gain': '增肌', 'shape': '保持' };
    const newPlan: IDietPlan = {
      id: generatePlanId(),
      name: planData.name || `方案 ${plans.length + 1}`,
      goal: (planData.goal || profile.goal) as IDietPlan['goal'],
      planTemplate: (planData.planTemplate || 'generic') as DietPlanTemplate,
      startDate: planData.startDate || Date.now(),
      tastePreference: planData.tastePreference || '正常',
      avoidFoods: planData.avoidFoods || [],
      allergens: planData.allergens || [],
      mealsPerDay: (planData.mealsPerDay || 4) as 3 | 4 | 5,
      supplements: planData.supplements || [],
      gender: (planData.gender || profile.gender) as IDietPlan['gender'],
      height: planData.height || profile.height,
      weight: planData.weight || profile.weight,
      weeklyHours: planData.weeklyHours || profile.weeklyTrainingHours || 4,
      bodyType: (planData.bodyType || profile.bodyType) as IDietPlan['bodyType'],
      carbDay: 'medium',
      trainingTimeSlot: profile.trainingTimeSlot,
      mealFoods: planData.mealFoods || {
        breakfast: [{ foodId: 'oatmeal', grams: 30 }, { foodId: 'egg', grams: 100 }, { foodId: 'skim-milk', grams: 250 }],
        lunch: [{ foodId: 'brown-rice', grams: 120 }, { foodId: 'chicken-breast', grams: 150 }, { foodId: 'broccoli', grams: 200 }],
        dinner: [{ foodId: 'sweet-potato', grams: 150 }, { foodId: 'salmon', grams: 120 }, { foodId: 'spinach', grams: 200 }],
        snack: [{ foodId: 'banana', grams: 120 }],
        'pre-workout': undefined,
        'post-workout': undefined,
      },
      aiWeekPlanGenerated: false,
      createdAt: Date.now(),
    };

    if (member.isMember) {
      // 会员：新增方案并切换到新方案
      setPlans(prev => [...prev, newPlan]);
    } else {
      // 普通用户：切换当前方案，保留旧方案历史
      setPlans(previous => [...previous, newPlan]);
    }
    setCurrentPlanId(newPlan.id);
    return newPlan;
  }, [member.isMember, plans.length, profile]);

  // 删除方案
  const deletePlan = useCallback((planId: string) => {
    setPlans(prev => {
      const next = prev.filter(p => p.id !== planId);
      if (planId === currentPlanId && next.length > 0) {
        setCurrentPlanId(next[0].id);
      }
      return next;
    });
  }, [currentPlanId]);

  // 切换当前方案
  const switchPlan = useCallback((planId: string) => {
    const selected = plans.find(p => p.id === planId);
    if (!selected || (selected.planTemplate !== 'generic' && (!member.isMember || selected.memberDietSuspended || isRetiredFatLoss(selected.planTemplate)))) return;
    setCurrentPlanId(planId);
  }, [plans, member.isMember, setCurrentPlanId]);

  // 按目标 + 会员状态自动选择方案模板
  // - 减脂：通用公式（用户后续可在方案选择页手动切专属方案）
  // - 增肌：会员→tan-muscle，非会员→通用增肌
  // - 塑形：会员→tan-shape，非会员→通用塑形
  const autoPickTemplateForGoal = (goal: 'fat-loss' | 'muscle-gain' | 'shape'): DietPlanTemplate => {
    if (goal === 'muscle-gain' && member.isMember) return 'tan-muscle';
    if (goal === 'shape' && member.isMember) return 'tan-shape';
    return 'generic';
  };

  // 按目标 + 模板生成方案显示名
  const getPlanDisplayName = (goal: 'fat-loss' | 'muscle-gain' | 'shape', template: DietPlanTemplate): string => {
    const meta = DIET_PLAN_TEMPLATES[template];
    if (template !== 'generic') return meta.name;
    // generic 方案名按目标区分
    if (goal === 'muscle-gain') return '通用增肌方案';
    if (goal === 'shape') return '通用塑形方案';
    return '通用减脂方案';
  };

  // 从测评结果创建/更新方案
  // mode = 'new': 新建一套并切换当前方案，历史档案保留
  // mode = 'edit': 更新当前方案（不新增）
  const savePlanFromAssessment = useCallback((data: {
    gender: 'male' | 'female';
    height: number;
    weight: number;
    weeklyHours: number;
    bodyType: 'slim' | 'normal' | 'overweight';
    goal: 'fat-loss' | 'muscle-gain' | 'shape';
    tastePreference: string;
    avoidFoods: string[];
    trainingTimeSlot?: 'morning-early' | 'morning' | 'noon' | 'afternoon' | 'evening' | 'night';
  }, mode?: 'new' | 'edit'): IDietPlan => {
    const goalLabel = { 'fat-loss': '减脂', 'muscle-gain': '增肌', 'shape': '塑形' }[data.goal];
    // 自动按目标分配模板（增肌/塑形会员直接上专属方案）
    const template = (() => {
      if (data.goal === 'muscle-gain' && member.isMember) return 'tan-muscle' as DietPlanTemplate;
      if (data.goal === 'shape' && member.isMember) return 'tan-shape' as DietPlanTemplate;
      return 'generic' as DietPlanTemplate;
    })();
    const templateName = getPlanDisplayName(data.goal, template);

    // edit 模式：更新当前方案
    if (mode === 'edit' && !currentPlan?.serviceNotice) {
      const target = currentPlan || plans[0];
      if (target) {
        const updated = {
          ...target,
          ...data,
          name: templateName,
          planTemplate: template,
          startDate: target.startDate || Date.now(),
          carbDay: target.carbDay || 'medium' as const,
          trainingTimeSlot: data.trainingTimeSlot || target.trainingTimeSlot || 'evening',
        };
        setPlans(prev => prev.map(p => p.id === target.id ? updated : p));
        // 同步写入 profile
        if (data.trainingTimeSlot) {
          setProfile(prev => ({ ...prev, trainingTimeSlot: data.trainingTimeSlot! }));
        }
        return updated;
      }
    }

    // new 模式 / 默认
    const targets = calcPlanDailyTargets({
      template,
      weight: data.weight,
      gender: data.gender,
      height: data.height,
      bodyType: data.bodyType,
      weeklyHours: data.weeklyHours,
      goal: data.goal,
      carbDay: 'medium',
    });
    const isPremiumPlan = DIET_PLAN_TEMPLATES[template]?.isPremiumPlan && member.isMember;
    const trainingTimeSlot = (data.trainingTimeSlot || profile.trainingTimeSlot || 'evening') as any;
    const mealPlan = generatePlanMealFoods(template, targets, data.gender, 1, {
      isTrainingDay: true,
      includeIndependentWorkoutMeals: isPremiumPlan,
      trainingTimeSlot,
    });
    const newPlan: IDietPlan = {
      id: generatePlanId(),
      name: templateName,
      goal: data.goal,
      planTemplate: template,
      startDate: Date.now(),
      tastePreference: data.tastePreference,
      avoidFoods: data.avoidFoods,
      allergens: (data as any).allergens || [],
      mealsPerDay: ((data as any).mealsPerDay || 4) as 3 | 4 | 5,
      supplements: (data as any).supplements || [],
      gender: data.gender,
      height: data.height,
      weight: data.weight,
      weeklyHours: data.weeklyHours,
      bodyType: data.bodyType,
      carbDay: 'medium',
      trainingTimeSlot: data.trainingTimeSlot || profile.trainingTimeSlot || 'evening',
      mealFoods: {
        breakfast: mealPlan.breakfast.map(m => ({ foodId: m.foodId, grams: m.grams })),
        lunch: mealPlan.lunch.map(m => ({ foodId: m.foodId, grams: m.grams })),
        dinner: mealPlan.dinner.map(m => ({ foodId: m.foodId, grams: m.grams })),
        snack: mealPlan.snack.map(m => ({ foodId: m.foodId, grams: m.grams })),
        'pre-workout': mealPlan.preWorkout?.map(m => ({ foodId: m.foodId, grams: m.grams })),
        'post-workout': mealPlan.postWorkout?.map(m => ({ foodId: m.foodId, grams: m.grams })),
      },
      aiWeekPlanGenerated: false,
      createdAt: Date.now(),
    };

    // 同步写入 profile 的训练时间
    if (data.trainingTimeSlot) {
      setProfile(prev => ({ ...prev, trainingTimeSlot: data.trainingTimeSlot! }));
    }

    if (member.isMember) {
      // 会员：追加新方案
      setPlans(prev => [...prev, newPlan]);
    } else {
      // 非会员：只有 1 套，覆盖
      setPlans(previous => [...previous, newPlan]);
    }
    setCurrentPlanId(newPlan.id);
    return newPlan;
  }, [member.isMember, plans, currentPlan]);

  // 切换方案模板（会员用，将当前方案切换为指定专属方案模板）
  // 重新生成每日目标和三餐食材，保留用户基础信息（体重/性别/身高/训练时长等）
  const activatePlanTemplate = useCallback((template: DietPlanTemplate, startDate?: string, activityFactor?: number, assessmentPlan?: IDietPlan): IDietPlan | null => {
    const target = assessmentPlan || currentPlan || plans[0];
    let scheduledPlan: ITrainingPlan | null = null;
    try { scheduledPlan = JSON.parse(userStorage.getItem('member_training_plan') || 'null'); } catch { /* Old or unavailable cache falls back to profile. */ }
    const nutritionProfile = assessmentPlan ? { ...profile, ...assessmentPlan, weeklyTrainingHours: assessmentPlan.weeklyHours }
      : { ...profile, weeklyTrainingHours: member.isMember ? trainingNutritionHours(profile.weeklyTrainingHours, scheduledPlan) : profile.weeklyTrainingHours };
    if (!target) return null;
    if (!member.isMember && template !== 'generic') return null;
    if (isRetiredFatLoss(template)) return null;
    if (target.planTemplate === template && !target.serviceNotice) return target;
    if (isAttachedFatLoss(template) && attachedPlanIssue({ template, weight: nutritionProfile.weight, height: nutritionProfile.height,
      age: nutritionProfile.age, weeklyHours: nutritionProfile.weeklyTrainingHours })) return null;

    const meta = DIET_PLAN_TEMPLATES[template];
    // 正确推导目标分类：根据模板本身决定 goal（减脂类模板保持原 goal）
    const derivedGoal: IDietPlan['goal'] =
      template === 'tan-muscle' ? 'muscle-gain' :
      template === 'tan-shape' ? 'shape' :
      isAttachedFatLoss(template) ? 'fat-loss' : target.goal || 'fat-loss';
    const displayName = getPlanDisplayName(derivedGoal, template);
    const nutrition = isAttachedFatLoss(template) ? {
      weight: nutritionProfile.weight, gender: nutritionProfile.gender, height: nutritionProfile.height,
      bodyType: nutritionProfile.bodyType, weeklyHours: nutritionProfile.weeklyTrainingHours,
    } : { weight: target.weight, gender: target.gender, height: target.height,
      bodyType: target.bodyType, weeklyHours: target.weeklyHours };

    const targets = calcPlanDailyTargets({
      template,
      age: nutritionProfile.age,
      activityFactor: activityFactor ?? target.activityFactor,
      ...nutrition,
      goal: derivedGoal,
      carbDay: target.carbDay,
      dayIndex: 1,
    });
    const isPremiumPlan = DIET_PLAN_TEMPLATES[template]?.isPremiumPlan && member.isMember;
    const trainingTimeSlot = target.trainingTimeSlot || profile.trainingTimeSlot;
    const mealPlan = generatePlanMealFoods(template, targets, nutrition.gender, 1, {
      isTrainingDay: true,
      includeIndependentWorkoutMeals: isPremiumPlan,
      trainingTimeSlot,
    });
    const mealFoods = {
      breakfast: mealPlan.breakfast.map(m => ({ foodId: m.foodId, grams: m.grams })),
      lunch: mealPlan.lunch.map(m => ({ foodId: m.foodId, grams: m.grams })),
      dinner: mealPlan.dinner.map(m => ({ foodId: m.foodId, grams: m.grams })),
      snack: mealPlan.snack.map(m => ({ foodId: m.foodId, grams: m.grams })),
      'pre-workout': mealPlan.preWorkout?.map(m => ({ foodId: m.foodId, grams: m.grams })),
      'post-workout': mealPlan.postWorkout?.map(m => ({ foodId: m.foodId, grams: m.grams })),
    };

    const updated: IDietPlan = {
      ...target,
      ...nutrition,
      id: generatePlanId(),
      age: nutritionProfile.age,
      activityFactor: activityFactor ?? target.activityFactor,
      memberDietSuspended: false,
      memberAccessUntil: member.expireDate,
      serviceNotice: undefined,
      createdAt: Date.now(),
      planTemplate: template,
      startDate: planStartDateTimestamp(startDate ?? localTrainingDate()),
      dailyMealFoods: {},
      dailySnapshots: {},
      weeklyHoursHistory: assessmentPlan ? undefined : futureNutritionHours(nutrition.weeklyHours, scheduledPlan, localTrainingDate()),
      name: displayName,
      goal: derivedGoal,
      mealFoods,
    };

    setPlans(prev => [...prev, updated]);
    setCurrentPlanId(updated.id);
    return updated;
  }, [member.isMember, member.expireDate, currentPlan, plans, profile, setCurrentPlanId, setPlans]);

  // 保存私人订制问卷（会员专属）：同时写 profile 和当前 diet plan
  // - 重叠字段（身高/体重/性别/目标/训练时间/忌口/口味）以私人订制问卷为准覆盖
  // - 新增字段（伤病部位/过敏原/餐次/补剂）写入 profile 和 plan 两边
  // - 返回更新后的 plan
  const saveDetailedAssessment = useCallback((data: {
    age?: number;
    sessionMinutes?: number;
    gender: 'male' | 'female';
    height: number;
    weight: number;
    goalWeight?: number;
    bodyType: 'slim' | 'normal' | 'overweight';
    goal: 'fat-loss' | 'muscle-gain' | 'shape';
    trainingMonths: number;
    trainingFrequency: '1-2' | '3-4' | '5-6';
    weeklyHours: number;
    trainingTimeSlot: 'morning-early' | 'morning' | 'noon' | 'afternoon' | 'evening' | 'night';
    trainingVenue?: 'gym' | 'home';
    injuryParts: string[];
    avoidFoods: string[];
    allergens: string[];
    tastePreferenceList: string[];
    mealsPerDay: 3 | 4 | 5;
    supplements: string[];
    strengthDays?: number[];
    planStartDate?: string;
    sleepQuality?: 'good' | 'normal' | 'poor';
    sleepHours?: number;
    sleepBedtime?: 'early' | 'normal' | 'late' | 'very-late';
  } & PersonalSchedule): IDietPlan | null => {
    if (!member.isMember) return null;
    // 1. 写入 profile
    const hasInjury = data.injuryParts.length > 0;
    setProfile(prev => ({
      ...prev,
      ...scheduleFields(data),
      age: data.age ?? prev.age,
      sessionMinutes: data.sessionMinutes ?? prev.sessionMinutes,
      gender: data.gender,
      height: data.height,
      weight: data.weight,
      goalWeight: data.goalWeight ?? prev.goalWeight,
      bodyType: data.bodyType,
      goal: data.goal,
      trainingMonths: data.trainingMonths,
      trainingFrequency: data.trainingFrequency,
      weeklyTrainingHours: data.weeklyHours,
      trainingTimeSlot: data.trainingTimeSlot,
      trainingPlace: data.trainingVenue || 'gym',
      hasInjury,
      injuryParts: data.injuryParts,
      injuryDetails: hasInjury ? [...prev.injuryDetails, ...data.injuryParts] : prev.injuryDetails,
      allergens: data.allergens, // 兼容历史数据，不再展示
      // 过敏原并入忌口食物（统一用 avoidFoods 驱动餐单替换）
      avoidFoods: Array.from(new Set([...(data.allergens || []), ...data.avoidFoods])),
      tastePreferenceList: data.tastePreferenceList,
      mealsPerDay: data.mealsPerDay,
      supplements: data.supplements,
      strengthDays: data.strengthDays || prev.strengthDays,
      planStartDate: data.planStartDate ?? prev.planStartDate,
      sleepQuality: data.sleepQuality ?? prev.sleepQuality,
      sleepHours: data.sleepHours ?? prev.sleepHours,
      sleepBedtime: data.sleepBedtime ?? prev.sleepBedtime,
    }));

    // 2. 写入 diet plan（更新当前方案，保留原模板与餐食生成结果，只覆盖字段）
    const target = currentPlan || plans[0];
    const tastePrefStr = data.tastePreferenceList[0] || '正常';

    if (target) {
      const updated: IDietPlan = {
        ...target,
        ...scheduleFields(data),
        id: target.serviceNotice ? generatePlanId() : target.id,
        ...(target.serviceNotice ? { dailySnapshots: {}, dailyMealFoods: {} } : {}),
        serviceNotice: undefined,
        memberDietSuspended: false,
        memberAccessUntil: member.expireDate,
        startDate: planStartDateTimestamp(data.planStartDate),
        gender: data.gender,
        age: data.age ?? target.age,
        height: data.height,
        weight: data.weight,
        ...assessmentWeeklyHours(target, data.weeklyHours, localTrainingDate()),
        bodyType: data.bodyType,
        goal: data.goal,
        trainingTimeSlot: data.trainingTimeSlot,
        trainingVenue: data.trainingVenue || 'gym',
        tastePreference: tastePrefStr,
        // 过敏原并入忌口食物（统一用 avoidFoods 驱动餐单替换）
        avoidFoods: data.avoidFoods.length > 0 || data.allergens?.length > 0
          ? Array.from(new Set([...(data.allergens || []), ...data.avoidFoods]))
          : target.avoidFoods,
        allergens: data.allergens, // 兼容历史数据，不再展示
        mealsPerDay: data.mealsPerDay,
        supplements: data.supplements,
        fromDetailedAssessment: true,
      };
      setPlans(prev => target.serviceNotice ? [...prev, updated] : prev.map(p => p.id === target.id ? updated : p));
      if (target.serviceNotice) setCurrentPlanId(updated.id);
      return updated;
    }

    // 没有现有方案时，按私人订制结果新建一套通用减脂方案
    const templateName = getPlanDisplayName(data.goal, 'generic' as DietPlanTemplate);
    const targets = calcPlanDailyTargets({
      template: 'generic' as DietPlanTemplate,
      weight: data.weight,
      gender: data.gender,
      height: data.height,
      bodyType: data.bodyType,
      weeklyHours: data.weeklyHours,
      goal: data.goal,
      carbDay: 'medium',
    });
    const mealPlan = generatePlanMealFoods('generic' as DietPlanTemplate, targets, data.gender);
    const newPlan: IDietPlan = {
      id: generatePlanId(),
      ...scheduleFields(data),
      name: templateName,
      goal: data.goal,
      planTemplate: 'generic' as DietPlanTemplate,
      age: data.age,
      startDate: planStartDateTimestamp(data.planStartDate),
      tastePreference: tastePrefStr,
      // 过敏原并入忌口食物（统一用 avoidFoods 驱动餐单替换）
      avoidFoods: Array.from(new Set([...(data.allergens || []), ...data.avoidFoods])),
      allergens: data.allergens, // 兼容历史数据，不再展示
      mealsPerDay: data.mealsPerDay,
      supplements: data.supplements,
      gender: data.gender,
      height: data.height,
      weight: data.weight,
      weeklyHours: data.weeklyHours,
      bodyType: data.bodyType,
      carbDay: 'medium',
      trainingTimeSlot: data.trainingTimeSlot,
      trainingVenue: data.trainingVenue || 'gym',
      mealFoods: {
        breakfast: mealPlan.breakfast.map(m => ({ foodId: m.foodId, grams: m.grams })),
        lunch: mealPlan.lunch.map(m => ({ foodId: m.foodId, grams: m.grams })),
        dinner: mealPlan.dinner.map(m => ({ foodId: m.foodId, grams: m.grams })),
        snack: mealPlan.snack.map(m => ({ foodId: m.foodId, grams: m.grams })),
      },
      aiWeekPlanGenerated: false,
      fromDetailedAssessment: true,
      createdAt: Date.now(),
    };
    if (member.isMember) {
      setPlans(prev => [...prev, newPlan]);
    } else {
      setPlans(previous => [...previous, newPlan]);
    }
    setCurrentPlanId(newPlan.id);
    return newPlan;
  }, [member.isMember, currentPlan, plans]);

  return {
    // 多方案 API
    plans,
    savedCurrentPlan,
    currentPlan,
    currentPlanId,
    hasPlans: plans.length > 0,
    switchPlan,
    addPlan,
    deletePlan,
    // 向后兼容（单方案 API，操作当前方案）
    plan,
    hasPlan,
    updatePlan,
    captureDietDay,
    savePlanFromAssessment,
    activatePlanTemplate,
    saveDetailedAssessment,
  };
}

// ==================== 今日饮食目标（统一数据源：首页 / 饮食页今日tab 共用） ====================

export interface ITodayDiet {
  snapshot?: DietDaySnapshot;
  /** 今日碳日类型 */
  carbDay: 'high' | 'medium' | 'low';
  /** 碳日标签，如「高碳日」 */
  carbDayLabel: string;
  /** 碳日子标签，如「训练日」「过渡日」「休息日」 */
  carbDaySubLabel: string;
  /** 是否训练日（高碳日） */
  isTrainingDay: boolean;
  /** 当日营养目标（按今日碳日计算） */
  targets: {
    calories: number;
    carb: number;
    protein: number;
    fat: number;
    salt: number;
    waterMl?: number;
    isHighCarbDay?: boolean;
    stageLabel?: string;
  } | null;
  /** 今天是方案第几天 */
  dayIndex: number;
}

// 默认力量日（按训练频率）
function defaultStrengthDaysForFreq(freq: string): number[] {
  if (freq === '1-2') return [1, 5];
  if (freq === '5-6') return [1, 2, 4, 5, 6];
  return [1, 3, 5]; // 3-4
}

// 有氧日推导（力量日次日 + 周末）
function deriveCardioDays(strengthDays: number[], count: number): number[] {
  const set = new Set(strengthDays);
  const result: number[] = [];
  // 力量日后一天优先
  for (const d of strengthDays) {
    const next = d === 7 ? 1 : d + 1;
    if (!set.has(next) && !result.includes(next)) {
      result.push(next);
      if (result.length >= count) return result;
    }
  }
  // 补周末
  for (const d of [7, 6, 3]) {
    if (!set.has(d) && !result.includes(d)) {
      result.push(d);
      if (result.length >= count) return result;
    }
  }
  return result;
}

// 普通用户碳日推导
function calcRegularCarbDay(
  trainingFrequency: string,
  goal: string,
  strengthDays?: number[],
): { type: 'high' | 'medium' | 'low'; subLabel: string } {
  const dow = new Date().getDay();
  const dayOfWeek = dow === 0 ? 7 : dow;
  const days = strengthDays?.length ? strengthDays : defaultStrengthDaysForFreq(trainingFrequency);
  if (days.includes(dayOfWeek)) return { type: 'high', subLabel: '训练日' };
  const cardioCount = goal === 'fat-loss' ? 3 : goal === 'shape' ? 3 : 2;
  const cardioDays = deriveCardioDays(days, cardioCount);
  if (cardioDays.includes(dayOfWeek)) return { type: 'medium', subLabel: '过渡日' };
  return { type: 'low', subLabel: '休息日' };
}

export function useTodayDiet(): ITodayDiet {
  const { plan, plans } = useDietPlans();
  const { profile } = useUserProfile();
  const { member } = useMemberStatus();
  const { memberPlan, getTodayWeekDay } = useMemberTrainingPlan();
  const memberDay = member.isMember && !plan?.serviceNotice && memberPlan ? getTodayWeekDay() : null;
  return resolveDietDate(new Date(), { plan, plans, profile, memberDay,
    memberCarbDay: memberDay ? getCarbDayFromTrainingDay(memberDay.type) : undefined });
}

export function useUserProfile() {
  const [profile, setProfile] = useSyncedState<IUserProfile>(PROFILE_KEY, () => {
    try {
      const raw = userStorage.getItem(PROFILE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown as Record<string, unknown>;
        // 兼容旧数据：goal=保持 (maintain) → 归一化为塑形 (shape)
        // 保持与塑形在热量策略上均为 TDEE×1.0，塑形定位更符合产品定位
        if (parsed.goal === 'maintain') {
          parsed.goal = 'shape';
        }
        return parsed as unknown as IUserProfile;
      }
      return DEFAULT_PROFILE;
    } catch {
      return DEFAULT_PROFILE;
    }
  });

  const updateProfile = useCallback((updates: Partial<IUserProfile>) => {
    setProfile(prev => ({ ...prev, ...updates }));
  }, [setProfile]);

  return { profile, setProfile, updateProfile };
}

// 会员状态由服务器管理；独立 hook 避免继续扩张业务状态文件。
export { useMemberStatus } from './useMemberStatus';
export type { IMemberInfo } from './useMemberStatus';

// ==================== 训练记录 ====================

export interface ISetRecord {
  setNum: number;
  weight: number;  // kg, 自重动作=0
  reps: number;
  completed: boolean;
  /** 本组感受：easy=轻松 / moderate=适中 / hard=费力 */
  feeling?: 'easy' | 'moderate' | 'hard';
}

export interface IExerciseRecord {
  skipped?: boolean;
  exerciseId: string;
  exerciseName: string;
  sets: ISetRecord[];
  feeling?: 'easy' | 'moderate' | 'hard';
}

export interface ITrainingRecord {
  sessionStatus?: 'completed' | 'partial';
  memberPlanId?: string;
  sourceKind?: string;
  replacedDayId?: string;
  countsTowardPlan?: boolean;
  id: string;
  date: string;         // YYYY-MM-DD
  courseId?: string;
  courseName?: string;
  dayId?: string;
  dayName?: string;
  durationMin: number;
  totalSets: number;
  completedSets: number;
  exercises: IExerciseRecord[];
  volumeReductionPercent?: number;
}

// ==================== PR（个人最好成绩） ====================

export interface IPRRecord {
  exerciseId: string;
  exerciseName: string;
  /** 最好成绩的最大重量（kg） */
  maxWeight: number;
  /** 最大重量下的最多次数 */
  maxReps: number;
  /** 突破日期 YYYY-MM-DD */
  recordDate: string;
  /** 训练记录 id */
  recordId: string;
}

/**
 * 从一组动作记录中计算最佳单组成绩（自重动作 weight=0 时按次数比较）
 * 比较规则：先比重量（大者胜），重量相同比次数（多者胜）
 */
function getBestSet(exercise: IExerciseRecord): { weight: number; reps: number } | null {
  const completed = exercise.sets.filter(s => s.completed && s.reps > 0);
  if (completed.length === 0) return null;
  let best = completed[0];
  for (let i = 1; i < completed.length; i++) {
    const s = completed[i];
    if (s.weight > best.weight) { best = s; continue; }
    if (s.weight === best.weight && s.reps > best.reps) { best = s; continue; }
    // 自重场景（weight===0）按次数比
    if (s.weight === 0 && best.weight === 0 && s.reps > best.reps) { best = s; }
  }
  return { weight: best.weight, reps: best.reps };
}

const TRAINING_KEY = 'training_records';

export function useTrainingRecords() {
  const [records, setRecords] = useSyncedState<ITrainingRecord[]>(TRAINING_KEY, () => {
    try {
      const raw = userStorage.getItem(TRAINING_KEY);
      if (!raw) return DEFAULT_TRAINING_RECORDS;
      return JSON.parse(raw);
    } catch {
      return DEFAULT_TRAINING_RECORDS;
    }
  });

  const addRecord = useCallback((record: ITrainingRecord) => {
    setRecords(prev => [record, ...prev]);
  }, [setRecords]);

  const deleteRecord = useCallback((recordId: string) => {
    setRecords(prev => prev.filter(r => r.id !== recordId));
  }, [setRecords]);

  const getLastRecordForExercise = useCallback((exerciseId: string): IExerciseRecord | undefined => {
    for (const r of records) {
      // 跳过降量日：降量日的训练重量/次数偏低，不能作为下次正常训练的参考
      if (r.volumeReductionPercent && r.volumeReductionPercent > 0) continue;
      const ex = r.exercises.find(e => matchesLibraryHistoryExercise(e.exerciseId, exerciseId));
      if (ex) return ex;
    }
    return undefined;
  }, [records]);

  // 获取某动作上次训练记录的日期（YYYY-MM-DD），用于停练检测
  const getLastRecordDateForExercise = useCallback((exerciseId: string): string | undefined => {
    for (const r of records) {
      if (r.volumeReductionPercent && r.volumeReductionPercent > 0) continue;
      if (r.exercises.some(e => matchesLibraryHistoryExercise(e.exerciseId, exerciseId))) return r.date;
    }
    return undefined;
  }, [records]);

  // 获取某动作的重量建议（仅会员使用，非会员调用方自行不展示）
  const getWeightSuggestionForExercise = useCallback((
    exerciseId: string,
    bodyPart: string | undefined,
    repsStr: string,
    isBodyweight: boolean,
  ): WeightSuggestion | null => {
    const last = getLastRecordForExercise(exerciseId);
    const base = getWeightSuggestion(last, bodyPart, repsStr, isBodyweight);
    if (!base) return null;

    // 停练超 14 天 → 覆盖为 80% 起步
    const lastDate = getLastRecordDateForExercise(exerciseId);
    const layoff = detectLayoff(lastDate);
    if (layoff.isLayoff && base.suggestedWeight > 0) {
      const recoveryWeight = Math.round(base.suggestedWeight * layoff.suggestedFactor * 2) / 2; // 保留 0.5 精度
      return {
        suggestedWeight: Math.max(2.5, recoveryWeight),
        direction: 'down',
        delta: -(base.suggestedWeight - recoveryWeight),
        reason: `停练 ${layoff.daysSinceLast} 天，建议 80% 起步`,
        detail: '循序渐进恢复，避免受伤',
      };
    }

    // 连续 2 次训练存在未达标组 → 减重 + 加强警示
    const consecutive = getConsecutiveUnderperformCount(recordsForLibraryHistoryQuery(records, exerciseId), exerciseId, repsStr);
    if (consecutive >= 2 && base.direction === 'down') {
      return {
        ...base,
        reason: `连续 ${consecutive} 次有组未达标，先减重`,
        detail: '建议检查休息、饮食与动作质量，切勿硬冲重量',
      };
    }

    return base;
  }, [records, getLastRecordForExercise, getLastRecordDateForExercise]);

  // 批量计算本次训练所有动作的下次建议（用于完成页汇总）
  const getWorkoutSuggestionSummary = useCallback((
    exercises: Array<{ exerciseId: string; bodyPart?: string; repsStr: string; isBodyweight?: boolean }>,
  ): { up: number; maintain: number; down: number; total: number } => {
    let up = 0, maintain = 0, down = 0, total = 0;
    for (const ex of exercises) {
      const s = getWeightSuggestionForExercise(ex.exerciseId, ex.bodyPart, ex.repsStr, !!ex.isBodyweight);
      if (!s) continue;
      total++;
      if (s.direction === 'up') up++;
      else if (s.direction === 'down') down++;
      else maintain++;
    }
    return { up, maintain, down, total };
  }, [getWeightSuggestionForExercise]);

  // ========== PR（个人最好成绩）计算 ==========
  const prRecords = useMemo<IPRRecord[]>(() => {
    const map = new Map<string, IPRRecord>();
    // 按日期从旧到新遍历，后出现的覆盖（新纪录会更新）
    const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
    for (const rec of sorted) {
      for (const ex of rec.exercises) {
        const best = getBestSet(ex);
        if (!best) continue;
        // 自重动作(weight===0) 跳过 PR（只统计有重量的）
        if (best.weight === 0) continue;
        const prev = map.get(ex.exerciseId);
        if (!prev
          || best.weight > prev.maxWeight
          || (best.weight === prev.maxWeight && best.reps > prev.maxReps)
        ) {
          map.set(ex.exerciseId, {
            exerciseId: ex.exerciseId,
            exerciseName: ex.exerciseName,
            maxWeight: best.weight,
            maxReps: best.reps,
            recordDate: rec.date,
            recordId: rec.id,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [records]);

  const getPRForExercise = useCallback((exerciseId: string): IPRRecord | undefined => {
    return findCompatibleLibraryPR(prRecords, exerciseId);
  }, [prRecords]);

  /**
   * 检测一次新训练中哪些动作刷新了 PR（与训练前的历史 PR 比较）
   * @param newRecord 刚完成的训练记录
   * @returns 本次刷新的 PR 列表
   */
  const detectNewPRs = useCallback((newRecord: ITrainingRecord): IPRRecord[] => {
    // 历史 PR（去掉本次记录的贡献——用除本次外的所有记录重算）
    const historyRecords = records.filter(r => r.id !== newRecord.id);
    const historyMap = new Map<string, IPRRecord>();
    const sorted = [...historyRecords].sort((a, b) => a.date.localeCompare(b.date));
    for (const rec of sorted) {
      for (const ex of rec.exercises) {
        const best = getBestSet(ex);
        if (!best || best.weight === 0) continue;
        const prev = historyMap.get(ex.exerciseId);
        if (!prev
          || best.weight > prev.maxWeight
          || (best.weight === prev.maxWeight && best.reps > prev.maxReps)
        ) {
          historyMap.set(ex.exerciseId, {
            exerciseId: ex.exerciseId,
            exerciseName: ex.exerciseName,
            maxWeight: best.weight,
            maxReps: best.reps,
            recordDate: rec.date,
            recordId: rec.id,
          });
        }
      }
    }
    const newPRs: IPRRecord[] = [];
    for (const ex of newRecord.exercises) {
      const best = getBestSet(ex);
      if (!best || best.weight === 0) continue;
      const prev = findCompatibleLibraryPR(Array.from(historyMap.values()), ex.exerciseId);
      if (!prev
        || best.weight > prev.maxWeight
        || (best.weight === prev.maxWeight && best.reps > prev.maxReps)
      ) {
        newPRs.push({
          exerciseId: ex.exerciseId,
          exerciseName: ex.exerciseName,
          maxWeight: best.weight,
          maxReps: best.reps,
          recordDate: newRecord.date,
          recordId: newRecord.id,
        });
      }
    }
    return newPRs;
  }, [records]);

  return {
    records, setRecords, addRecord, deleteRecord,
    getLastRecordForExercise, getLastRecordDateForExercise,
    getWeightSuggestionForExercise, getWorkoutSuggestionSummary,
    prRecords, getPRForExercise, detectNewPRs,
  };
}

// Mock 默认训练记录（已移除——新用户显示空态引导）
const _dateStrLegacy = (offsetDays: number) => {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d.toISOString().slice(0, 10);
};
void _dateStrLegacy;

const DEFAULT_TRAINING_RECORDS: ITrainingRecord[] = [];

// ==================== 体重记录 ====================

export interface IWeightRecord {
  date: string;
  weight: number;
}

const WEIGHT_KEY = 'weight_records';

const DEFAULT_WEIGHT_RECORDS: IWeightRecord[] = [];

export function useWeightRecords() {
  const [weights, setWeights] = useSyncedState<IWeightRecord[]>(WEIGHT_KEY, () => {
    try {
      const raw = userStorage.getItem(WEIGHT_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_WEIGHT_RECORDS;
    } catch {
      return DEFAULT_WEIGHT_RECORDS;
    }
  });

  const addWeight = useCallback((weight: number) => {
    const today = new Date().toISOString().slice(0, 10);
    setWeights(prev => {
      const existing = prev.findIndex(w => w.date === today);
      if (existing >= 0) {
        const copy = [...prev];
        copy[existing] = { date: today, weight };
        return copy;
      }
      return [...prev, { date: today, weight }].sort((a, b) => a.date.localeCompare(b.date));
    });
  }, []);

  return { weights, setWeights, addWeight };
}

// ==================== 每日健康记录 ====================
// 会员每日录入：晨脉、睡眠时长、体重，按日期记录，首页健康卡读取最新值

export interface IDailyHealthRecord {
  date: string;          // YYYY-MM-DD
  restingHeartRate?: number; // 静息心率 bpm
  sleepHours?: number;   // 睡眠时长 h
  weight?: number;       // 体重 kg
  mood?: 'great' | 'normal' | 'poor'; // 心情
}

const DAILY_HEALTH_KEY = 'daily_health_records';

// useDailyHealth 已迁移到 src/hooks/useDailyHealth.tsx（Context 全局单例，支持跨组件同步更新）
// 此处仅保留 IDailyHealthRecord 类型定义供其他模块引用

// ==================== 打卡 streak（基于训练记录动态计算） ====================

export function useStreak() {
  const { records } = useTrainingRecords();

  const streak = useMemo(() => {
    if (records.length === 0) return 0;
    const dates = new Set(records.map(r => r.date));
    const d = new Date();
    // 找到最近一个有训练记录的日子
    let checked = 0;
    while (checked < 365) {
      const dateStr = localTrainingDate(d);
      if (dates.has(dateStr)) break;
      d.setDate(d.getDate() - 1);
      checked++;
    }
    if (checked >= 365) return 0;
    // 从最近打卡日往回连续计数
    let count = 0;
    while (true) {
      const dateStr = localTrainingDate(d);
      if (dates.has(dateStr)) {
        count++;
        d.setDate(d.getDate() - 1);
      } else {
        break;
      }
    }
    return count;
  }, [records]);

  const lastCheckin = useMemo(() => {
    if (records.length === 0) return '';
    const dates = records.map(r => r.date).sort((a, b) => b.localeCompare(a));
    return dates[0] || '';
  }, [records]);

  const checkin = useCallback(() => {
    // 兼容保留：训练完成时自动写入记录即完成打卡，不再单独维护 streak
  }, []);

  return { streak, lastCheckin, checkin };
}

// ==================== 训练进行中状态 ====================
// 实现已迁移到 useActiveWorkout.tsx（Context 全局单例）
// 此处仅做 re-export，保持 import { useActiveWorkout } from '@/hooks/useAppState' 兼容
export { useActiveWorkout } from './useActiveWorkout';
export type { IActiveWorkout } from './useActiveWorkout';

// ==================== 定制方案 ====================

export interface ICustomPlan {
  hasPlan: boolean;
  courseId: string;
  courseName: string;
  system: CourseSystem;
  goal: 'fat-loss' | 'muscle-gain' | 'shape'; // 训练目标
  dietPlanTemplate?: string; // 关联的饮食方案模板（与 IDietPlan.planTemplate 对应）
  cycleDay: number;        // 本周期第几天
  cycleTotalDays: number;  // 每周期总天数
  currentCycle: number;    // 第几个周期
  baseWeight: number;      // 体重基数
  carbCoefficient: number; // 碳水系数 g/kg
  startDate: string;
  adjusted: boolean;       // 是否有过调整
  adjustmentHistory: IAdjustmentRecord[];
}

export interface IAdjustmentRecord {
  id: string;
  date: string;
  type: 'morning-pulse' | 'weight-stall' | 'weight-fast' | 'poor-condition';
  description: string;
  carbChange?: number;
  volumeReduction?: number; // 降量百分比
}

const PLAN_KEY = 'custom_plan';

const DEFAULT_PLAN: ICustomPlan = {
  hasPlan: false,
  courseId: '',
  courseName: '',
  system: 'three-way',
  goal: 'fat-loss',
  dietPlanTemplate: 'three-month-2026',
  cycleDay: 1,
  cycleTotalDays: 10,
  currentCycle: 1,
  baseWeight: 0,
  carbCoefficient: 3.0,
  startDate: '',
  adjusted: false,
  adjustmentHistory: [],
};

export function useCustomPlan() {
  const [plan, setPlan] = useSyncedState<ICustomPlan>(PLAN_KEY, () => {
    try {
      const raw = userStorage.getItem(PLAN_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_PLAN;
    } catch {
      return DEFAULT_PLAN;
    }
  });
  const { generateMemberPlan, generateRecoveryPlan } = useMemberTrainingPlan();

  const advanceCycle = useCallback(() => {
    setPlan(prev => ({
      ...prev,
      currentCycle: prev.currentCycle + 1,
      cycleDay: 1,
      carbCoefficient: Math.max(1.5, +(prev.carbCoefficient - 0.5).toFixed(1)),
      adjusted: true,
    }));
  }, []);

  /**
   * 10 天周期动态优化：基于体重记录 + 晨脉信号 + 主观状态
   * 规则（减脂目标）：
   *   周降 0.5-1% → 保持（最佳速率）
   *   周降 < 0.3%（体重不掉）→ 碳水 -0.2g/kg
   *   周降 > 1.5%（掉太快）→ 碳水 +0.3g/kg
   *   连续 7 天晨脉高于基线 8+ → 当日降量 20% + 写警告
   *   主观状态差 → 碳水 +0.5g/kg
   * 增肌目标：反向（涨太慢加碳水，涨太快减碳水）
   */
  const runDynamicOptimization = useCallback((params: {
    weights: { date: string; weight: number }[];
    previewOnly?: boolean;
    restingHeartRate?: number;
    /** 晨脉基线（7 天均值），不传则从数据中推 */
    hrBaseline?: number;
    /** 主观状态：great / good / normal / poor */
    condition?: 'great' | 'good' | 'normal' | 'poor';
  }): { adjustments: IAdjustmentRecord[]; newCarbCoefficient: number; volumeReductionPercent?: number } => {
    const { weights, restingHeartRate, hrBaseline, condition } = params;
    const carb = plan.carbCoefficient;
    if (isAttachedFatLoss(plan.dietPlanTemplate || '')) return { adjustments: [], newCarbCoefficient: carb };
    const goal = plan.goal;

    const adjustments: IAdjustmentRecord[] = [];
    let newCarb = carb;
    let volumeReduction: number | undefined;

    const today = new Date();
    const todayStr = localTrainingDate(today);

    // ===== 体重变化分析（最近 7 天 / 至少 2 个数据点）=====
    const sortedWeights = [...weights].sort((a, b) => a.date.localeCompare(b.date));
    const recentWeights = sortedWeights.filter(w => {
      const d = new Date(w.date);
      const diff = (today.getTime() - d.getTime()) / 86400000;
      return diff <= 14; // 取最近 14 天数据做趋势
    });

    if (recentWeights.length >= 2) {
      const startW = recentWeights[0].weight;
      const endW = recentWeights[recentWeights.length - 1].weight;
      const diffDays = Math.max(1,
        (new Date(recentWeights[recentWeights.length - 1].date).getTime()
         - new Date(recentWeights[0].date).getTime()) / 86400000
      );
      const weeklyChange = ((endW - startW) / startW) * (7 / diffDays) * 100; // 百分比/周

      if (goal === 'fat-loss') {
        if (weeklyChange > -0.3 && weeklyChange <= 0.2) {
          // 体重不掉（平台期）→ 减碳水
          const decrease = 0.2;
          newCarb = +Math.max(1.0, newCarb - decrease).toFixed(1);
          adjustments.push({
            id: `adj-weight-stall-${todayStr}`,
            date: todayStr,
            type: 'weight-stall',
            description: `体重平台（周变化 ${weeklyChange > 0 ? '+' : ''}${weeklyChange.toFixed(2)}%）→ 碳水 -${decrease}g/kg`,
            carbChange: -decrease,
          });
        } else if (weeklyChange <= -1.5) {
          // 掉太快 → 加碳水
          const increase = 0.3;
          newCarb = +Math.min(6.0, newCarb + increase).toFixed(1);
          adjustments.push({
            id: `adj-weight-fast-${todayStr}`,
            date: todayStr,
            type: 'weight-fast',
            description: `体重下降过快（周降 ${Math.abs(weeklyChange).toFixed(1)}%）→ 碳水 +${increase}g/kg，防止肌肉流失`,
            carbChange: increase,
          });
        } else {
          // 合理范围（周降 0.5-1%）→ 保持
          adjustments.push({
            id: `adj-weight-ok-${todayStr}`,
            date: todayStr,
            type: 'weight-stall',
            description: `体重速率合理（周降 ${Math.abs(weeklyChange).toFixed(1)}%）→ 方案保持`,
            carbChange: 0,
          });
        }
      } else if (goal === 'muscle-gain') {
        if (weeklyChange < 0.2) {
          // 涨太慢 → 加碳水
          const increase = 0.3;
          newCarb = +Math.min(7.0, newCarb + increase).toFixed(1);
          adjustments.push({
            id: `adj-weight-slow-${todayStr}`,
            date: todayStr,
            type: 'weight-stall',
            description: `体重增长偏慢（周增 ${weeklyChange.toFixed(2)}%）→ 碳水 +${increase}g/kg`,
            carbChange: increase,
          });
        } else if (weeklyChange > 0.8) {
          // 涨太快 → 减碳水（防脂肪堆积）
          const decrease = 0.2;
          newCarb = +Math.max(2.0, newCarb - decrease).toFixed(1);
          adjustments.push({
            id: `adj-weight-fat-${todayStr}`,
            date: todayStr,
            type: 'weight-fast',
            description: `体重增长过快（周增 ${weeklyChange.toFixed(1)}%，脂肪堆积风险）→ 碳水 -${decrease}g/kg`,
            carbChange: -decrease,
          });
        } else {
          adjustments.push({
            id: `adj-weight-ok-${todayStr}`,
            date: todayStr,
            type: 'weight-stall',
            description: `增肌速率合理（周增 ${weeklyChange.toFixed(2)}%）→ 方案保持`,
            carbChange: 0,
          });
        }
      }
    }

    // ===== 晨脉分析 =====
    if (restingHeartRate !== undefined && hrBaseline !== undefined) {
      const diff = restingHeartRate - hrBaseline;
      if (diff >= 8) {
        volumeReduction = 20;
        adjustments.push({
          id: `adj-morning-pulse-${todayStr}`,
          date: todayStr,
          type: 'morning-pulse',
          description: `晨起心率高于基线 ${diff} 次/分 → 今日训练量降 20%，身体需要恢复`,
          volumeReduction: 20,
        });
      }
    }

    // ===== 主观状态 =====
    if (condition === 'poor') {
      const increase = 0.5;
      newCarb = +Math.min(goal === 'fat-loss' ? 3.5 : 7.0, newCarb + increase).toFixed(1);
      adjustments.push({
        id: `adj-condition-${todayStr}`,
        date: todayStr,
        type: 'poor-condition',
        description: `主观状态差（疲劳/睡不好/压力大）→ 碳水 +${increase}g/kg，优先保证恢复`,
        carbChange: increase,
      });
    }

    // ===== 写入计划 =====
    if (adjustments.length > 0 && !params.previewOnly) {
      setPlan(prev => {
        const existingIds = new Set(prev.adjustmentHistory.map(a => a.id));
        const newAdj = adjustments.filter(a => !existingIds.has(a.id));
        if (newAdj.length === 0) return prev;
        return {
          ...prev,
          carbCoefficient: newCarb,
          adjusted: true,
          adjustmentHistory: [...newAdj, ...prev.adjustmentHistory].slice(0, 50),
        };
      });
    }

    return { adjustments, newCarbCoefficient: newCarb, volumeReductionPercent: volumeReduction };
  }, [plan.carbCoefficient, plan.goal, plan.dietPlanTemplate]);

  // 根据私人订制问卷的目标 + 训练经验，自动匹配训练计划并更新定制方案
  // - 减脂：保留当前三分化方案（或用户已选方案）
  // - 增肌：按训练经验匹配（新手→三分化 / 1-3年→四分化 / 爱好者→精讲三分化）
  // - 塑形：按训练经验匹配（新手→三分化 / 1-3年→四分化，叠加体态改善标签）
  const applyGoalFromAssessment = useCallback((goal: 'fat-loss' | 'muscle-gain' | 'shape', trainingMonths: number, opts?: { dietTemplate?: string; sessionMinutes?: number; gender?: 'male' | 'female'; weeklyHours?: number; trainingPlace?: 'gym' | 'home'; trainingFrequency?: '1-2' | '3-4' | '5-6'; strengthDays?: number[]; startDate?: number; injuryParts?: string[]; sleepQuality?: 'good' | 'normal' | 'poor'; skipTrainingGeneration?: boolean }) => {
    const dietTemplate = (opts?.dietTemplate || 'three-month-2026') as any;
    const gender = opts?.gender || 'male';
    const weeklyHours = opts?.weeklyHours ?? 4;
    const trainingPlace = opts?.trainingPlace || 'gym';
    const trainingFrequency = opts?.trainingFrequency || '3-4';

    setPlan(prev => {
      let courseId = prev.courseId;
      let courseName = prev.courseName;
      let system: CourseSystem = prev.system;
      let carbCoefficient = prev.carbCoefficient;
      let cycleTotalDays = prev.cycleTotalDays;

      if (goal === 'muscle-gain') {
        // 增肌：按经验分档
        if (trainingMonths < 6) {
          courseId = 'three-way-collection';
          courseName = '三分化·推/拉/蹲 增肌版';
          system = 'three-way';
        } else if (trainingMonths < 24) {
          courseId = 'four-way-collection';
          courseName = '四分化·胸背腿肩臂 增肌版';
          system = 'four-way';
        } else {
          courseId = 'ksw-collection';
          courseName = '凯圣王合集 增肌版';
          system = 'ksw-collection';
        }
        carbCoefficient = 4.0;
        cycleTotalDays = 14; // 增肌周期 14 天
      } else if (goal === 'shape') {
        // 塑形：按经验分档（与 matchCourseSystem 对齐：<12月=新手、12-36月=老手、>36月=资深）
        if (trainingMonths < 12) {
          courseId = 'three-way-collection';
          courseName = '三分化·推/拉/蹲 塑形版';
          system = 'three-way';
        } else if (trainingMonths < 36) {
          courseId = 'four-way-collection';
          courseName = '四分化·胸背腿肩臂 塑形版';
          system = 'four-way';
        } else {
          courseId = 'ksw-collection';
          courseName = '凯圣王合集 塑形版';
          system = 'ksw-collection';
        }
        carbCoefficient = 2.5;
        cycleTotalDays = 10;
      } else {
        // 减脂：保留用户当前选择（默认三分化）
        courseId = prev.courseId || 'three-way-collection';
        courseName = prev.courseName || '三分化·推/拉/腿 减脂版';
        system = prev.system || 'three-way';
        carbCoefficient = 3.0;
        cycleTotalDays = 10;
      }

      // 方案开始日期：优先用传入值，否则用今天（日期字符串 YYYY-MM-DD）
      const newStartDate = localTrainingDate(opts?.startDate ? new Date(opts.startDate) : new Date());

      const result = {
        ...prev,
        courseId,
        courseName,
        system,
        goal,
        dietPlanTemplate: dietTemplate,
        carbCoefficient,
        cycleTotalDays,
        cycleDay: 1, // 存储基准值，展示用 cycleDayDisplay 派生
        currentCycle: 1, // 存储基准值，展示用 currentCycleDisplay 派生
        startDate: newStartDate,
        adjusted: false,
        adjustmentHistory: [],
      };

      // 同步生成会员专属训练周计划
       const mappedInjuryParts = (opts?.injuryParts ?? [])
         .map(p => INJURY_PART_MAP[p] || (INJURY_ACTION_POOL[p as InjuryPart] ? (p as InjuryPart) : null))
         .filter((p): p is InjuryPart => !!p);
       if (!opts?.skipTrainingGeneration) generateMemberPlan({
         goal,
         sessionMinutes: opts?.sessionMinutes,
         dietTemplate,
         trainingPlace,
         trainingFrequency,
         trainingMonths,
         weeklyHours,
         gender,
         strengthDays: opts?.strengthDays,
         startDate: opts?.startDate,
         injuryParts: mappedInjuryParts,
       });

      // 同步生成伤病恢复计划（独立模块，不影响主训练方案）
      // 睡眠差时追加助眠放松动作
      if (opts?.injuryParts && opts.injuryParts.length > 0) {
        generateRecoveryPlan(opts.injuryParts, opts.sleepQuality ?? 'normal');
      } else {
        // 无伤病时清空恢复计划，走通用恢复逻辑
        generateRecoveryPlan([], opts.sleepQuality ?? 'normal');
      }

      return result;
    });
  }, [generateMemberPlan, generateRecoveryPlan]);

  // ========== 周期天数自动计算（基于 startDate，日期驱动，无需打卡）==========
  // 与会员训练方案的 getTodayWeekDay 逻辑一致：
  //   从 startDate 起算，每天自动 +1 天
  //   满 cycleTotalDays 天后进下一周期，重新从第 1 天开始
  const cycleProgress = useMemo(() => {
    if (!plan.startDate || !plan.cycleTotalDays || plan.cycleTotalDays <= 0) {
      return { cycleDayDisplay: plan.cycleDay || 1, currentCycleDisplay: plan.currentCycle || 1 };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(plan.startDate);
    start.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((today.getTime() - start.getTime()) / msPerDay);
    // startDate 当天 = 第 1 天
    const totalDays = Math.max(0, diffDays) + 1;
    const total = plan.cycleTotalDays;
    const currentCycle = Math.floor((totalDays - 1) / total) + 1;
    const cycleDay = ((totalDays - 1) % total) + 1;
    return { cycleDayDisplay: cycleDay, currentCycleDisplay: currentCycle };
  }, [plan.startDate, plan.cycleTotalDays, plan.cycleDay, plan.currentCycle]);

  return {
    plan,
    setPlan,
    advanceCycle,
    applyGoalFromAssessment,
    runDynamicOptimization,
    ...cycleProgress,
  };
}

// ==================== 每日一句 ====================

const DAILY_QUOTES = [
  '微微饥饿 = 最佳状态，说明方案在正轨上',
  '训练不是越多越好，是够量就好',
  '坚持一周看不到变化，坚持一个月才开始蜕变',
  '吃好睡好，肌肉才会长',
  '今天不想练，就练到想练为止',
  '腹肌是在厨房里练出来的',
  '不要跟别人比，跟昨天的自己比',
  '每一次力竭，都是进步的开始',
  '跑步不减肥，饮食才减肥',
  '慢就是快，稳才是赢',
];

export function getDailyQuote(dateStr_: string = new Date().toISOString().slice(0, 10)): string {
  const dayNum = parseInt(dateStr_.replace(/-/g, '').slice(6), 10);
  return DAILY_QUOTES[dayNum % DAILY_QUOTES.length];
}

// ==================== 身体信号记录 ====================

export interface ISignalRecord {
   id: string;
   date: string;
   bodyPart: string;
   description: string;
   painLevel: 1 | 2 | 3; // 1轻微 2中等 3严重
   /** 恢复天数（天）：轻=3 / 中=7 / 重=14，可手动修改 */
   recoveryDays: number;
   /** 是否已确认到期（到期弹窗只弹一次，确认后不再弹） */
   expiryAcknowledged?: boolean;
   /** 是否已解除（手动点已恢复后移到历史，但保留记录） */
   resolved?: boolean;
   /** 解除日期 */
   resolvedDate?: string;
 }

const SIGNALS_KEY = 'body_signals';

const DEFAULT_SIGNALS: ISignalRecord[] = [];

 const DEFAULT_RECOVERY_DAYS: Record<1 | 2 | 3, number> = {
   1: 3,
   2: 7,
   3: 14,
 };

 /** 计算到期日（YYYY-MM-DD） */
 function calcExpiryDate(startDate: string, days: number): string {
   const d = new Date(startDate);
   d.setDate(d.getDate() + days);
   return d.toISOString().slice(0, 10);
 }

export function useBodySignals() {
  const { profile, updateProfile } = useUserProfile();
  const [signals, setSignals] = useState<ISignalRecord[]>(() => {
    try {
      const raw = userStorage.getItem(SIGNALS_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_SIGNALS;
    } catch {
      return DEFAULT_SIGNALS;
    }
  });

  useEffect(() => {
    userStorage.setItem(SIGNALS_KEY, JSON.stringify(signals));
  }, [signals]);

   // 当前活跃的伤病部位（未解除的，去重，英文 key）
   const activeInjuryParts = useMemo((): string[] => {
     const set = new Set<string>();
     for (const s of signals) {
       if (s.resolved) continue;
       if (s.bodyPart) set.add(s.bodyPart);
     }
     return Array.from(set);
   }, [signals]);

   // 当前活跃的不适记录（未解除的）
   const activeSignals = useMemo((): ISignalRecord[] => {
     return signals.filter(s => !s.resolved);
   }, [signals]);

   // 已解除的历史记录
   const resolvedSignals = useMemo((): ISignalRecord[] => {
     return signals.filter(s => s.resolved);
   }, [signals]);

   /** 剩余天数，正值=还有N天，0=今天到期，负值=已过期未处理 */
   function getRemainingDays(signal: ISignalRecord): number {
     const today = new Date().toISOString().slice(0, 10);
     const expiry = calcExpiryDate(signal.date, signal.recoveryDays);
     const diffDays = Math.floor(
       (new Date(expiry).getTime() - new Date(today).getTime()) / (1000 * 60 * 60 * 24),
     );
     return diffDays;
   }

   /** 获取到期日 */
   function getExpiryDate(signal: ISignalRecord): string {
     return calcExpiryDate(signal.date, signal.recoveryDays);
   }

   /** 今天到期且未确认的记录（用于弹窗提醒） */
   const todayExpiringSignals = useMemo((): ISignalRecord[] => {
     const today = new Date().toISOString().slice(0, 10);
     return signals.filter(s => {
       if (s.resolved) return false;
       if (s.expiryAcknowledged) return false;
       return calcExpiryDate(s.date, s.recoveryDays) <= today;
     });
   }, [signals]);

   /** 重度不适且仍在恢复期的部位 */
   const severeParts = useMemo((): string[] => {
     const set = new Set<string>();
     for (const s of signals) {
       if (s.resolved) continue;
       if (s.painLevel === 3) set.add(s.bodyPart);
     }
     return Array.from(set);
   }, [signals]);

  /**
   * 判断单个动作是否因伤病被规避
   * 返回 null = 不规避；返回 1/2/3 = 规避且对应疼痛等级
   */
  const getExerciseAvoidLevel = useCallback((exerciseId: string): 0 | 1 | 2 | 3 => {
    if (activeSignals.length === 0) return 0;
    const ex = getExerciseById(exerciseId);
    if (!ex) return 0;
    let maxLevel: 0 | 1 | 2 | 3 = 0;
    for (const sig of activeSignals) {
      const part = sig.bodyPart as InjuryPart;
      const rule = INJURY_AVOID_MAP[part];
      if (!rule) continue;
      let hit = false;
      if (rule.bodyParts?.includes(ex.bodyPart)) hit = true;
      if (ex.subCategory && rule.subCategories?.includes(ex.subCategory)) hit = true;
      if (rule.keywords?.some(kw => ex.name.includes(kw))) hit = true;
      if (hit && sig.painLevel > maxLevel) maxLevel = sig.painLevel;
    }
    return maxLevel;
  }, [activeSignals]);

  /** 批量获取被规避的动作 id 列表（按不适等级过滤，默认所有等级都规避） */
  const getAvoidedExercises = useCallback((exerciseIds: string[], minLevel: 1 | 2 | 3 = 1): string[] => {
    if (activeSignals.length === 0) return [];
    return exerciseIds.filter(id => getExerciseAvoidLevel(id) >= minLevel);
  }, [activeSignals, getExerciseAvoidLevel]);

  /**
   * 获取所有命中某动作的活跃伤病记录列表
   * 用于训练页点击「已恢复」时，同步把对应部位的伤病标记为已恢复
   */
  const getMatchingSignalsForExercise = useCallback((exerciseId: string): ISignalRecord[] => {
    if (activeSignals.length === 0) return [];
    const ex = getExerciseById(exerciseId);
    if (!ex) return [];
    return activeSignals.filter(sig => {
      const part = sig.bodyPart as InjuryPart;
      const rule = INJURY_AVOID_MAP[part];
      if (!rule) return false;
      if (rule.bodyParts?.includes(ex.bodyPart)) return true;
      if (ex.subCategory && rule.subCategories?.includes(ex.subCategory)) return true;
      if (rule.keywords?.some(kw => ex.name.includes(kw))) return true;
      return false;
    });
  }, [activeSignals]);

  // 同步伤病部位到 profile.injuryParts（中文 label，与问卷一致）
  const syncInjuryPartsToProfile = useCallback((parts: string[]) => {
    const labels = parts
      .map(p => INJURY_PART_LABEL_REVERSE[p as InjuryPart] || p)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    const current = profile.injuryParts || [];
    // 只有变化了才更新，避免无限循环
    if (current.sort().join(',') !== labels.sort().join(',')) {
      updateProfile({ injuryParts: labels });
    }
  }, [profile.injuryParts, updateProfile]);

  // 信号变化时同步到 profile
  useEffect(() => {
    syncInjuryPartsToProfile(activeInjuryParts);
  }, [activeInjuryParts, syncInjuryPartsToProfile]);

   const addSignal = useCallback((signal: Omit<ISignalRecord, 'id' | 'date' | 'recoveryDays'> & { recoveryDays?: number }) => {
     const today = new Date().toISOString().slice(0, 10);
     const recoveryDays = signal.recoveryDays ?? DEFAULT_RECOVERY_DAYS[signal.painLevel];
     const newSignal: ISignalRecord = {
       ...signal,
       recoveryDays,
       id: `sig-${Date.now()}`,
       date: today,
     };
     setSignals(prev => [newSignal, ...prev]);
   }, []);

   const resolveSignal = useCallback((id: string) => {
     const today = new Date().toISOString().slice(0, 10);
     setSignals(prev => prev.map(s =>
       s.id === id
         ? { ...s, resolved: true, resolvedDate: today, expiryAcknowledged: true }
         : s,
     ));
   }, []);

   /** 顺延恢复期（默认+3天，可自定义），并重置到期确认状态 */
   const extendRecovery = useCallback((id: string, extraDays: number) => {
     setSignals(prev => prev.map(s => {
       if (s.id !== id) return s;
       return {
         ...s,
         recoveryDays: s.recoveryDays + extraDays,
         expiryAcknowledged: false,
       };
     }));
   }, []);

   /** 标记已确认到期弹窗（避免重复弹） */
   const markExpiryAcknowledged = useCallback((id: string) => {
     setSignals(prev => prev.map(s =>
       s.id === id ? { ...s, expiryAcknowledged: true } : s,
     ));
   }, []);

   /** 修改恢复期天数 */
   const updateRecoveryDays = useCallback((id: string, days: number) => {
     setSignals(prev => prev.map(s =>
       s.id === id ? { ...s, recoveryDays: Math.max(1, days) } : s,
     ));
   }, []);

   return {
     signals,
     activeSignals,
     resolvedSignals,
     activeInjuryParts,
     severeParts,
     getExerciseAvoidLevel,
     getAvoidedExercises,
     getMatchingSignalsForExercise,
     todayExpiringSignals,
     addSignal,
     resolveSignal,
     extendRecovery,
     markExpiryAcknowledged,
     updateRecoveryDays,
     getRemainingDays,
     getExpiryDate,
     DEFAULT_RECOVERY_DAYS,
   };
 }

// ==================== 会员专属训练计划 ====================

export { useMemberTrainingPlan } from './useMemberTrainingPlan';
export { useTodayWorkout } from './useTodayWorkout';
export type { ITodayWorkoutInfo } from './useTodayWorkout';

// ==================== 训练周报 ====================

export { useWeeklyReport, getCurrentWeekMonday, offsetWeek } from './useWeeklyReport';
export type { IWeeklyReportData } from './useWeeklyReport';

// ==================== 常用食材统计 ====================

const FOOD_USAGE_KEY = 'food_usage_stats';

export interface IFoodUsageStat {
  foodId: string;
  /** 使用次数 */
  count: number;
  /** 最近一次使用日期 YYYY-MM-DD */
  lastUsedDate: string;
}

/**
 * 常用食材使用统计 hook
 * 每次添加食材到实际记录时累加使用次数，用于食材选择器「常用」排序
 */
export function useFoodUsageStats() {
  const [stats, setStats] = useSyncedState<IFoodUsageStat[]>(FOOD_USAGE_KEY, () => {
    try {
      const raw = userStorage.getItem(FOOD_USAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  /** 记录一次食材使用 */
  const recordFoodUsage = useCallback((foodId: string) => {
    const today = new Date().toISOString().slice(0, 10);
    setStats(prev => {
      const idx = prev.findIndex(s => s.foodId === foodId);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], count: copy[idx].count + 1, lastUsedDate: today };
        return copy;
      }
      return [...prev, { foodId, count: 1, lastUsedDate: today }];
    });
  }, [setStats]);

  /** 批量记录（一次添加多个食材） */
  const recordFoodUsageBatch = useCallback((foodIds: string[]) => {
    if (foodIds.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    setStats(prev => {
      const copy = [...prev];
      for (const foodId of foodIds) {
        const idx = copy.findIndex(s => s.foodId === foodId);
        if (idx >= 0) {
          copy[idx] = { ...copy[idx], count: copy[idx].count + 1, lastUsedDate: today };
        } else {
          copy.push({ foodId, count: 1, lastUsedDate: today });
        }
      }
      return copy;
    });
  }, [setStats]);

  /** 获取按使用次数降序排列的常用食材 id 列表（取前 N 个） */
  const getTopFoodIds = useCallback((limit = 8): string[] => {
    return [...stats]
      .sort((a, b) => b.count - a.count || b.lastUsedDate.localeCompare(a.lastUsedDate))
      .slice(0, limit)
      .map(s => s.foodId);
  }, [stats]);

  /** 获取指定食材的使用次数 */
  const getUsageCount = useCallback((foodId: string): number => {
    return stats.find(s => s.foodId === foodId)?.count || 0;
  }, [stats]);

  return { stats, recordFoodUsage, recordFoodUsageBatch, getTopFoodIds, getUsageCount };
}

// ==================== 上一餐复制工具函数 ====================

export interface ICopyLastMealResult {
  foods: { foodId: string; grams: number }[];
  /** 来源描述，如 "来自昨天早餐" */
  sourceLabel: string;
  /** 是否为同餐次复制 */
  isSameMealType: boolean;
}

/**
 * 查找最近一次同餐次的实际摄入记录
 * 优先同餐次（如早餐→早餐），没有则找最近一次任意餐次
 * 从昨天往前最多追溯 14 天
 */
export function findLastMealRecord(
  mealType: string,
  currentDate: Date = new Date()
): ICopyLastMealResult | null {
  const MEAL_LABELS: Record<string, string> = {
    breakfast: '早餐', lunch: '午餐', dinner: '晚餐', snack: '加餐',
  };

  // 从昨天往前追溯 14 天
  for (let offset = 1; offset <= 14; offset++) {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - offset);
    const dateKey = d.toDateString();
    try {
      const raw = userStorage.getItem(`diet_actual_${dateKey}`);
      if (!raw) continue;
      const record = JSON.parse(raw) as Record<string, { foodId: string; grams: number }[]>;

      // 同餐次优先
      const sameMealFoods = record[mealType];
      if (sameMealFoods && sameMealFoods.length > 0) {
        const label = offset === 1 ? '昨天' : `${offset}天前`;
        return {
          foods: sameMealFoods.map(f => ({ foodId: f.foodId, grams: f.grams })),
          sourceLabel: `来自${label}${MEAL_LABELS[mealType] || mealType}`,
          isSameMealType: true,
        };
      }
    } catch {
      // 跳过损坏的数据
    }
  }

  // 同餐次没找到，再找最近一次任意餐次
  for (let offset = 1; offset <= 14; offset++) {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - offset);
    const dateKey = d.toDateString();
    try {
      const raw = userStorage.getItem(`diet_actual_${dateKey}`);
      if (!raw) continue;
      const record = JSON.parse(raw) as Record<string, { foodId: string; grams: number }[]>;

      // 找最近有记录的餐次（按早餐→午餐→晚餐→加餐顺序）
      for (const mt of ['breakfast', 'lunch', 'dinner', 'snack']) {
        const foods = record[mt];
        if (foods && foods.length > 0) {
          const label = offset === 1 ? '昨天' : `${offset}天前`;
          return {
            foods: foods.map(f => ({ foodId: f.foodId, grams: f.grams })),
            sourceLabel: `来自${label}${MEAL_LABELS[mt] || mt}`,
            isSameMealType: false,
          };
        }
      }
    } catch {
      // skip
    }
  }

  return null;
}

// ==================== 转化触点追踪 ====================

interface IConversionTrack {
  /** 习惯养成弹窗是否已展示过（累计第3次训练后） */
  habitDialogShown: boolean;
  /** 会员权益回顾卡片最后展示日期（YYYY-MM-DD），到期前3天内每天展示一次 */
  renewalReviewLastShownDate: string;
}

const DEFAULT_CONVERSION: IConversionTrack = {
  habitDialogShown: false,
  renewalReviewLastShownDate: '',
};

const CONVERSION_KEY = 'conversion_track';

export function useConversionTrack() {
  const [track, setTrack] = useSyncedState<IConversionTrack>(CONVERSION_KEY, () => {
    try {
      const raw = userStorage.getItem(CONVERSION_KEY);
      if (raw) return { ...DEFAULT_CONVERSION, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_CONVERSION;
  });

  const markHabitDialogShown = useCallback(() => {
    setTrack(prev => ({ ...prev, habitDialogShown: true }));
  }, [setTrack]);

  const markRenewalReviewShown = useCallback(() => {
    setTrack(prev => ({ ...prev, renewalReviewLastShownDate: new Date().toISOString().slice(0, 10) }));
  }, [setTrack]);

  return {
    track,
    markHabitDialogShown,
    markRenewalReviewShown,
  };
}

/**
 * 计算近 N 个月的训练成果汇总（用于会员到期前权益回顾）
 */
export function useMemberRenewalSummary(months: number = 3) {
  const { records, prRecords } = useTrainingRecords();
  const { weights } = useWeightRecords();

  const summary = useMemo(() => {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - months);
    const startStr = startDate.toISOString().slice(0, 10);
    const todayStr = now.toISOString().slice(0, 10);

    // 近 N 个月训练记录
    const recentRecords = records.filter(r => r.date >= startStr && r.date <= todayStr);

    // 训练天数（去重日期）
    const trainingDays = new Set(recentRecords.map(r => r.date)).size;

    // 周报数量：按周计数
    const weekSet = new Set<string>();
    for (const r of recentRecords) {
      const d = new Date(r.date);
      const day = d.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = new Date(d);
      monday.setDate(d.getDate() + diffToMonday);
      weekSet.add(monday.toISOString().slice(0, 10));
    }
    const weeklyReportCount = weekSet.size;

    // 新 PR 数量
    const newPRCount = prRecords.filter(p => p.recordDate >= startStr && p.recordDate <= todayStr).length;

    // 体重变化：近 N 个月第一条 vs 最近一条
    const recentWeights = weights.filter(w => w.date >= startStr).sort((a, b) => a.date.localeCompare(b.date));
    let weightChange: number | undefined;
    let startWeight: number | undefined;
    let latestWeight: number | undefined;
    if (recentWeights.length >= 2) {
      startWeight = recentWeights[0].weight;
      latestWeight = recentWeights[recentWeights.length - 1].weight;
      weightChange = Number((latestWeight - startWeight).toFixed(1));
    } else if (recentWeights.length === 1) {
      startWeight = latestWeight = recentWeights[0].weight;
      weightChange = 0;
    }

    // 总训练次数
    const totalWorkouts = recentRecords.length;

    return {
      trainingDays,
      weeklyReportCount,
      newPRCount,
      weightChange,
      startWeight,
      latestWeight,
      totalWorkouts,
      startDate: startStr,
      endDate: todayStr,
    };
  }, [records, prRecords, weights, months]);

  return summary;
}

export default {};
